/**
 * Réglages d'un paquet principal (spécification §3, ADR 0010).
 *
 * Les valeurs par défaut doivent rester identiques à celles de la table
 * `reglages_paquets` (supabase/migrations).
 */

export type OrdreCartes =
  /** Nouvelles cartes et cartes dues piochées et présentées dans l'ordre d'ajout. */
  | 'chronologique'
  /** Piochées et présentées au hasard. */
  | 'hasard'
  /** Probabilité de rappel croissante ; nouvelles cartes exclues. */
  | 'probabilite_rappel'
  /** Toutes les cartes du paquet, ordre chronologique, limites quotidiennes ignorées. */
  | 'toutes_chronologique';

export type AffichageMinuteur = 'numerique' | 'camembert' | 'masque';

/**
 * Un passage d'une liste d'étapes. Durées en minutes, saisies explicitement.
 * `bien` est absent au dernier passage : Bien y rend la carte acquise (durée FSRS).
 */
export interface Passage {
  difficile: number;
  bien?: number;
}

/**
 * Étapes d'apprentissage ou de réapprentissage.
 * `aRevoir` : durée de 1 (À revoir), saisie une fois pour toute la liste ;
 * `null` uniquement quand la liste n'a aucun passage.
 */
export interface Etapes {
  aRevoir: number | null;
  passages: Passage[];
}

export interface ReglagesPaquet {
  afficherFilAriane: boolean;
  ordreCartes: OrdreCartes;
  /** Seuil d'alerte (ordre par probabilité de rappel). `null` : objectif de mémorisation. */
  seuilAlerte: number | null;
  nouvellesCartesParJour: number;
  revisionsMaxParJour: number;
  /** Probabilité visée (desired retention FSRS), entre 0 et 1 exclus. */
  objectifMemorisation: number;
  intervalleMaximumJours: number;
  etapesApprentissage: Etapes;
  etapesReapprentissage: Etapes;
  optimisationFsrs: boolean;
  /** Paramètres FSRS optimisés (21 valeurs pour FSRS-6). `null` : paramètres par défaut. */
  parametresFsrs: number[] | null;
  /** Heure (0 à 23) à laquelle commence la journée du paquet. */
  heureDebutJournee: number;
  fenetreDebordementMinutes: number;
  /** Retournement automatique après ce nombre de secondes. `null` : désactivé. */
  retournementAutoSecondes: number | null;
  affichageMinuteur: AffichageMinuteur;
}

/** Durée maximale d'une étape : 365 jours, en minutes. */
export const DUREE_ETAPE_MAXIMUM = 365 * 24 * 60;

export const ETAPES_APPRENTISSAGE_PAR_DEFAUT: Etapes = {
  aRevoir: 1,
  passages: [{ difficile: 5, bien: 10 }, { difficile: 5 }],
};

export const ETAPES_REAPPRENTISSAGE_PAR_DEFAUT: Etapes = {
  aRevoir: 10,
  passages: [{ difficile: 15 }],
};

export const REGLAGES_PAR_DEFAUT: ReglagesPaquet = {
  afficherFilAriane: true,
  ordreCartes: 'hasard',
  seuilAlerte: null,
  nouvellesCartesParJour: 20,
  revisionsMaxParJour: 200,
  objectifMemorisation: 0.9,
  intervalleMaximumJours: 36500,
  etapesApprentissage: ETAPES_APPRENTISSAGE_PAR_DEFAUT,
  etapesReapprentissage: ETAPES_REAPPRENTISSAGE_PAR_DEFAUT,
  optimisationFsrs: false,
  parametresFsrs: null,
  heureDebutJournee: 4,
  fenetreDebordementMinutes: 20,
  retournementAutoSecondes: null,
  affichageMinuteur: 'numerique',
};

/** Seuil d'alerte effectif : le seuil saisi, sinon l'objectif de mémorisation. */
export function seuilAlerteEffectif(reglages: ReglagesPaquet): number {
  return reglages.seuilAlerte ?? reglages.objectifMemorisation;
}

function estEntierDansIntervalle(valeur: unknown, min: number, max: number): valeur is number {
  return typeof valeur === 'number' && Number.isInteger(valeur) && valeur >= min && valeur <= max;
}

function estDureeEtape(valeur: unknown): valeur is number {
  return estEntierDansIntervalle(valeur, 1, DUREE_ETAPE_MAXIMUM);
}

/** Vérifie une liste d'étapes. Renvoie la liste des erreurs (vide si valide). */
export function validerEtapes(etapes: Etapes): string[] {
  const erreurs: string[] = [];
  const n = etapes.passages.length;
  if (n === 0) {
    if (etapes.aRevoir !== null)
      erreurs.push('À revoir doit être vide quand il n’y a aucun passage.');
    return erreurs;
  }
  if (!estDureeEtape(etapes.aRevoir)) erreurs.push('Durée de À revoir invalide.');
  etapes.passages.forEach((passage, i) => {
    const numero = i + 1;
    if (!estDureeEtape(passage.difficile)) {
      erreurs.push(`Durée de Difficile invalide au passage ${numero}.`);
    }
    if (i < n - 1) {
      if (!estDureeEtape(passage.bien))
        erreurs.push(`Durée de Bien invalide au passage ${numero}.`);
    } else if (passage.bien !== undefined) {
      erreurs.push('Au dernier passage, Bien rend la carte acquise : pas de durée à saisir.');
    }
  });
  return erreurs;
}

/** Vérifie tous les réglages. Renvoie la liste des erreurs (vide si valides). */
export function validerReglages(reglages: ReglagesPaquet): string[] {
  const erreurs: string[] = [];
  const probabilite = (valeur: number) => Number.isFinite(valeur) && valeur > 0 && valeur < 1;
  if (!probabilite(reglages.objectifMemorisation)) {
    erreurs.push('L’objectif de mémorisation doit être compris entre 0 et 100 % exclus.');
  }
  if (reglages.seuilAlerte !== null && !probabilite(reglages.seuilAlerte)) {
    erreurs.push('Le seuil d’alerte doit être compris entre 0 et 100 % exclus.');
  }
  if (!estEntierDansIntervalle(reglages.nouvellesCartesParJour, 0, 100000)) {
    erreurs.push('Nombre de nouvelles cartes par jour invalide.');
  }
  if (!estEntierDansIntervalle(reglages.revisionsMaxParJour, 0, 100000)) {
    erreurs.push('Nombre maximum de révisions par jour invalide.');
  }
  if (!estEntierDansIntervalle(reglages.intervalleMaximumJours, 1, 36500)) {
    erreurs.push('Intervalle maximum invalide (de 1 jour à 100 ans).');
  }
  if (!estEntierDansIntervalle(reglages.heureDebutJournee, 0, 23)) {
    erreurs.push('Heure de début de journée invalide (de 0 à 23 h).');
  }
  if (!estEntierDansIntervalle(reglages.fenetreDebordementMinutes, 0, DUREE_ETAPE_MAXIMUM)) {
    erreurs.push('Fenêtre de débordement invalide.');
  }
  if (
    reglages.retournementAutoSecondes !== null &&
    !estEntierDansIntervalle(reglages.retournementAutoSecondes, 1, 3600)
  ) {
    erreurs.push('Durée de retournement automatique invalide.');
  }
  if (
    reglages.parametresFsrs !== null &&
    (reglages.parametresFsrs.length !== 21 || !reglages.parametresFsrs.every(Number.isFinite))
  ) {
    erreurs.push('Les paramètres FSRS doivent compter 21 nombres.');
  }
  for (const [nom, etapes] of [
    ['apprentissage', reglages.etapesApprentissage],
    ['réapprentissage', reglages.etapesReapprentissage],
  ] as const) {
    for (const erreur of validerEtapes(etapes)) erreurs.push(`Étapes de ${nom} : ${erreur}`);
  }
  return erreurs;
}
